import { useEffect, useState } from 'react'; // Import the useEffect and useState hooks

import _ from 'lodash';
import { startOfRollupPeriod } from 'src/utils/rollup';
import { db } from 'src/utils/firebase';
import normalizeDate from 'src/utils/normalizeDate';
import { APPLY_BUDGET_PATCHES_TO_PAST_SEGMENTS } from 'src/utils/constants';

// Function to fetch budget patches using a snapshot listener
export const useBudgetPatches = (userId) => {
  const [budgets, setBudgets] = useState([]);

  useEffect(() => {
    if (!userId) return () => {};

    // Fetch budget patches using a snapshot listener
    console.log('fetching budget patches...');

    // Set up the query with a where clause and ordering
    const query = db
      .collection('budget_patch')
      .where('user_id', '==', userId)
      .orderBy('timestamp', 'asc');

    // Attach the snapshot listener and store the unsubscribe function
    const unsubscribe = query.onSnapshot((querySnapshot) => {
      console.log('budget patches updated...');
      const budgetPatches = [];
      querySnapshot.forEach((doc) => {
        budgetPatches.push({ id: doc.id, ...doc.data() });
      });

      // ? Only include patches that start from next month onwards
      // const endOfMonth = dateFns.endOfMonth(new Date());
      // const filtered = budgetPatches.filter((b) => {
      //   const patchDate = b.timestamp.toDate();
      //   return patchDate >= endOfMonth || !!b.scenario;
      // })
      setBudgets(budgetPatches);
    });

    // Clean up the listener when the component unmounts or when userId changes
    return () => {
      unsubscribe();
    };
  }, [userId]);

  return budgets;
};

// create a budget and apply its patches for data forecasting
export default function useBudget(series, period, patches, segments) {
  const start = segments[0].value;

  // create a basic budget based on their historical transactions
  const derivedBudget = createBudget(period, series, start);

  return patchesInSegments(segments, period, patches, derivedBudget);
}

export function patchesInSegments(segments, period, patches, derivedBudget) {
  // create an array of forward-looking budgets
  let budgets = [derivedBudget].filter(Boolean);

  (
    segments?.filter(
      (s) => s.future || (APPLY_BUDGET_PATCHES_TO_PAST_SEGMENTS && (s.current || s.past))
    ) ?? []
  )?.forEach((s) => {
    const patchesInSegment =
      patches?.filter(
        (p) => startOfRollupPeriod(p.timestamp.toDate(), period).getTime() === s.value.getTime()
      ) ?? [];

    if (patchesInSegment?.length > 0) {
      budgets = [
        ...budgets,
        applyPatchesToBudget(s, patchesInSegment, budgets[budgets.length - 1]),
      ];
    }
  });

  return budgets;
}

const applyPatchesToBudget = (segment, patches, budget) => {
  const result = {
    ...budget,
    patches,
    timestamp: segment.value,
  };

  patches.forEach((patch) => {
    const newCategories = {};

    Object.entries(patch.categories).forEach(([category, value]) => {
      if (!newCategories[category] && value.type !== 'onetime') {
        newCategories[category] = value.amount;
        // console.log('new recurring patch... ', segment.label, patch);
      } else {
        // ! IMPORTANT: Not adding to category map (!onetime | exists)
        // console.log('One time patch... ', segment.label, patch);
      }
    });

    result.categories = { ...result.categories, ...newCategories };
  });

  return result;
};

// create a budget model representing spending for a specific rollup period (month, qtr, yr)
const createBudget = (period, data, date) => {
  const budgetModel = { categories: {}, timestamp: date };

  Object.entries(data).forEach(([name, { average }]) => {
    budgetModel.categories[name] = parseFloat(average?.toFixed(2));
  });

  budgetModel.period = period;
  budgetModel.total = parseFloat(
    Object.values(budgetModel.categories)
      .reduce((acc, cur) => acc + cur, 0)
      .toFixed(2)
  );

  return budgetModel;
};

const sanitizePatch = (patch) => {
  // Clone the original patch to avoid mutating it directly
  const results = { ...patch };
  // Filter the categories dictionary where values are not null and meet the criteria
  results.categories = Object.fromEntries(
    Object.entries(results.categories || {}).filter(
      ([category, { amount }]) =>
        amount !== null && !Number.isNaN(amount) && amount !== undefined && amount !== null
    )
  );

  return results;
};

// fetch data from firebase
export const saveBudgetPatches = async (patchesToUpdate) => {
  try {
    const batch = db.batch();
    const sanitizedPatches = {};
    const firstPatch = patchesToUpdate[0];

    // Get existing patches from DB
    const existingPatches = await fetchExistingPatches(firstPatch?.user_id, patchesToUpdate);
    Object.assign(sanitizedPatches, existingPatches);

    // Consolidate patches
    patchesToUpdate.map(sanitizePatch).forEach((patch) => {
      consolidatePatch(patch, sanitizedPatches, batch);
    });

    // Process final patches
    processFinalPatches(sanitizedPatches, batch);

    await batch.commit();
    console.log('Budget patches updated successfully');
  } catch (error) {
    console.error('Error updating budget patches:', error);
    throw error;
  }
};

// Helper functions
const fetchExistingPatches = async (userId, patches) => {
  const monthStarts = [...new Set(patches.map((p) => normalizeDate(p.timestamp)))];
  // If no patches exist, return empty object
  if (!monthStarts.length) {
    return {};
  }

  // Break the monthStarts array into chunks of 30
  const chunkSize = 30;
  const monthStartChunks = Array(Math.ceil(monthStarts.length / chunkSize))
    .fill()
    .map((e, index) => monthStarts.slice(index * chunkSize, (index + 1) * chunkSize));

  // Process each chunk and combine results
  const results = {};

  await Promise.all(
    monthStartChunks.map(async (chunk) => {
      const snapshot = await db
        .collection('budget_patch')
        .where('user_id', '==', userId)
        .where(
          'timestamp',
          'in',
          chunk.map((ms) => new Date(ms))
        )
        .get();

      Object.assign(
        results,
        Object.fromEntries(snapshot.docs.map((doc) => [doc.id, { id: doc.id, ...doc.data() }]))
      );
    })
  );

  return results;
};

const consolidatePatch = (patch, sanitizedPatches, batch) => {
  const patchMonth = patch?.timestamp?.toDate()?.setDate(1);
  const existingPatch = findExistingPatchForMonth(patchMonth, patch.scenario, sanitizedPatches);

  if (existingPatch) {
    mergePatchWithExisting(patch, existingPatch, sanitizedPatches, batch);
  } else {
    sanitizedPatches[patch?.id ?? 'new'] = patch;
  }
};

const findExistingPatchForMonth = (patchMonth, scenario, patches) =>
  Object.values(patches)?.find(
    (p) =>
      p.timestamp?.toDate()?.setDate(1) === patchMonth &&
      // Only match patches with the same scenario
      p.scenario === scenario
  );

const mergePatchWithExisting = (patch, existingPatch, sanitizedPatches, batch) => {
  // Ensure we keep the scenario information when merging
  sanitizedPatches[existingPatch.id] = {
    ...existingPatch,
    scenario: patch.scenario, // Preserve scenario information
    categories: { ...existingPatch.categories, ...patch.categories },
  };

  if (patch.id && patch.id !== existingPatch.id) {
    batch.delete(db.collection('budget_patch').doc(patch.id));
  }
};

const processFinalPatches = (patches, batch) => {
  Object.values(patches).forEach((patch) => {
    if (!patch?.id) {
      if (!_.isEmpty(patch?.categories)) {
        batch.set(db.collection('budget_patch').doc(), patch);
      }
      return;
    }

    const patchRef = db.collection('budget_patch').doc(patch.id);
    if (_.isEmpty(patch?.categories)) {
      batch.delete(patchRef);
    } else {
      batch.update(patchRef, patch, { merge: true });
    }
  });
};
